import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiUrl, getApiBase } from "../lib/apiBase";
import { resolveBadgeImage } from "../lib/badges";
import "./PublicProfile.css";

function getToken() {
  try {
    return (
      localStorage.getItem("ga_token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("token") ||
      localStorage.getItem("jwt") ||
      ""
    );
  } catch {
    return "";
  }
}

function toAssetUrl(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^(?:https?:|data:|blob:)/i.test(raw)) return raw;
  const base = getApiBase().replace(/\/+$/, "");
  return `${base}${raw.startsWith("/") ? "" : "/"}${raw}`;
}

function externalHref(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw.replace(/^\/+/, "")}`;
}

function prettyTime(value) {
  const ts = Number(value || 0);
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return "";
  }
}

function WallPost({ post, token, currentUserId, profile, onDeleted }) {
  const [liked, setLiked] = useState(Boolean(post?.likedByMe));
  const [likeCount, setLikeCount] = useState(Math.max(0, Number(post?.likeCount || 0)));
  const [busy, setBusy] = useState(false);
  const authorUsername = String(post?.username || "member");
  const authorName = String(post?.displayName || authorUsername);
  const avatar = toAssetUrl(post?.authorAvatarUrl || "");
  const canDelete =
    currentUserId &&
    (String(post?.userId || "") === String(currentUserId) ||
      String(post?.targetUserId || "") === String(currentUserId));

  async function toggleLike() {
    if (!token) {
      window.location.href = "/login";
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      const response = await fetch(apiUrl(`/posts/${encodeURIComponent(post.id)}/like`), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.message || data?.error || "Could not update like.");
      setLiked(Boolean(data?.likedByMe));
      setLikeCount(Math.max(0, Number(data?.likeCount || 0)));
    } catch (error) {
      window.alert(error?.message || "Could not update like.");
    } finally {
      setBusy(false);
    }
  }

  async function removePost() {
    if (!canDelete || busy) return;
    if (!window.confirm("Remove this post from the profile?")) return;
    setBusy(true);
    try {
      const response = await fetch(
        apiUrl(
          `/public-profiles/${encodeURIComponent(profile.username)}/wall/${encodeURIComponent(
            post.id
          )}`
        ),
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.message || data?.error || "Could not remove post.");
      onDeleted?.(post.id);
    } catch (error) {
      window.alert(error?.message || "Could not remove post.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="ga-profile-wall-post" id={`wall-post-${post.id}`}>
      <div className="ga-profile-wall-post-head">
        {avatar ? (
          <img src={avatar} alt="" />
        ) : (
          <div className="fallback">{authorName.slice(0, 1).toUpperCase()}</div>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 950, overflowWrap: "anywhere" }}>
            <a href={`/u/${encodeURIComponent(authorUsername)}`} style={{ color: "inherit" }}>
              {authorName}
            </a>
            <span style={{ opacity: 0.5, fontWeight: 700 }}> → </span>
            <a
              href={`/u/${encodeURIComponent(profile.username)}`}
              style={{ color: "var(--profile-accent)" }}
            >
              {profile.displayName}
            </a>
          </div>
          <div style={{ opacity: 0.62, fontSize: 12 }}>
            @{authorUsername}
            {post?.createdAt ? ` • ${prettyTime(post.createdAt)}` : ""}
          </div>
        </div>
      </div>

      <div className="ga-profile-wall-post-text">{post?.text || post?.caption || ""}</div>

      <div className="ga-profile-wall-post-actions">
        <button
          className="ga-public-profile-btn"
          type="button"
          onClick={toggleLike}
          disabled={busy}
          aria-pressed={liked}
        >
          {liked ? "♥" : "♡"} Like {likeCount ? `(${likeCount})` : ""}
        </button>
        <button
          className="ga-public-profile-btn"
          type="button"
          onClick={() => {
            window.location.href = `/feed#post-${encodeURIComponent(post.id)}`;
          }}
        >
          💬 Feed {Number(post?.commentCount || 0) ? `(${post.commentCount})` : ""}
        </button>
        {canDelete ? (
          <button className="ga-public-profile-btn" type="button" onClick={removePost} disabled={busy}>
            Remove
          </button>
        ) : null}
      </div>
    </article>
  );
}

export default function PublicProfile() {
  const nav = useNavigate();
  const { username = "" } = useParams();
  const [token, setToken] = useState(() => getToken());
  const [currentUserId, setCurrentUserId] = useState("");
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({ authoredPosts: 0, wallPosts: 0 });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wallLoading, setWallLoading] = useState(true);
  const [error, setError] = useState("");
  const [wallText, setWallText] = useState("");
  const [wallBusy, setWallBusy] = useState(false);
  const [wallMessage, setWallMessage] = useState("");

  useEffect(() => {
    const refresh = () => setToken(getToken());
    window.addEventListener("auth:changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("auth:changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (!token) {
      setCurrentUserId("");
      return undefined;
    }
    fetch(apiUrl("/profile/me"), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => response.json().then((data) => ({ response, data })))
      .then(({ response, data }) => {
        if (!active || !response.ok) return;
        setCurrentUserId(String(data?.user?.id || ""));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [token]);

  async function loadProfile() {
    const handle = String(username || "").trim();
    if (!handle) return;
    setLoading(true);
    setError("");
    try {
      const response = await fetch(apiUrl(`/public-profiles/${encodeURIComponent(handle)}`), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.error === "profile_not_found" ? "That member profile was not found." : "Could not load profile."
        );
      }
      setProfile(data.profile || null);
      setStats(data.stats || { authoredPosts: 0, wallPosts: 0 });
    } catch (loadError) {
      setProfile(null);
      setError(loadError?.message || "Could not load profile.");
    } finally {
      setLoading(false);
    }
  }

  async function loadWall() {
    const handle = String(username || "").trim();
    if (!handle) return;
    setWallLoading(true);
    try {
      const response = await fetch(
        apiUrl(`/public-profiles/${encodeURIComponent(handle)}/wall`),
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.ok) throw new Error(data?.error || "profile_wall_failed");
      setPosts(Array.isArray(data.posts) ? data.posts : []);
    } catch {
      setPosts([]);
    } finally {
      setWallLoading(false);
    }
  }

  useEffect(() => {
    loadProfile();
    loadWall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, token]);

  useEffect(() => {
    if (wallLoading || !posts.length || !window.location.hash.startsWith("#wall-post-")) return;
    const id = window.location.hash.slice(1);
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [posts, wallLoading]);

  async function submitWallPost() {
    if (!token) {
      nav("/login");
      return;
    }
    const text = String(wallText || "").trim();
    if (!text || wallBusy || !profile?.username) return;

    setWallBusy(true);
    setWallMessage("");
    try {
      const response = await fetch(
        apiUrl(`/public-profiles/${encodeURIComponent(profile.username)}/wall`),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ text }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.error === "profile_post_too_long"
            ? "Profile posts can be up to 700 characters."
            : data?.message || data?.error || "Could not post."
        );
      }
      setWallText("");
      setWallMessage("Posted to the profile and the community Feed.");
      setPosts((current) => [data.post, ...current]);
      setStats((current) => ({ ...current, wallPosts: Number(current.wallPosts || 0) + 1 }));
      window.setTimeout(() => setWallMessage(""), 2600);
    } catch (postError) {
      setWallMessage(postError?.message || "Could not post.");
    } finally {
      setWallBusy(false);
    }
  }

  const theme = String(profile?.profileTheme || "gas");
  const accent = String(profile?.profileAccentColor || "#e10613");
  const background = toAssetUrl(profile?.profileBackgroundUrl || "");
  const mode = String(profile?.profileBackgroundMode || "cover");
  const badgeImage = useMemo(() => {
    if (!profile) return "";
    return resolveBadgeImage(
      profile.equippedBadgeKey,
      profile.customBadgeUrl,
      getApiBase()
    );
  }, [profile]);

  const socials = useMemo(() => {
    const raw = profile?.socials && typeof profile.socials === "object" ? profile.socials : {};
    return Object.entries(raw).filter(([, value]) => String(value || "").trim());
  }, [profile]);

  if (loading) {
    return <div className="ga-public-profile-error">Loading member profile…</div>;
  }

  if (error || !profile) {
    return (
      <div className="ga-public-profile-error">
        <h2 style={{ marginTop: 0 }}>Profile unavailable</h2>
        <div style={{ opacity: 0.82 }}>{error || "That member profile was not found."}</div>
        <button className="ga-public-profile-btn" style={{ marginTop: 14 }} onClick={() => nav("/feed")}>
          Back to Feed
        </button>
      </div>
    );
  }

  const isOwner = currentUserId && String(currentUserId) === String(profile.id);
  const avatar = toAssetUrl(profile.avatarUrl);
  const initial = String(profile.displayName || profile.username || "?").slice(0, 1).toUpperCase();

  return (
    <main
      className={`ga-public-profile-page theme-${theme} mode-${mode}`}
      style={{
        "--profile-accent": accent,
        "--profile-background": background ? `url("${background}")` : "none",
      }}
    >
      <div className="ga-public-profile-bg" />
      <div className="ga-public-profile-shade" />

      <div className="ga-public-profile-content">
        <div className="ga-public-profile-topbar">
          <button className="ga-public-profile-btn" type="button" onClick={() => nav("/feed")}>
            ← Feed
          </button>
          <div className="ga-public-profile-actions">
            {isOwner ? (
              <button
                className="ga-public-profile-btn primary"
                type="button"
                onClick={() => nav("/me#public-profile-customizer")}
              >
                Customize my profile
              </button>
            ) : null}
          </div>
        </div>

        <section className="ga-public-profile-hero">
          <div className="ga-public-profile-avatar">
            {avatar ? <img className="avatar" src={avatar} alt={profile.displayName} /> : initial}
            {badgeImage ? <img className="badge" src={badgeImage} alt="" /> : null}
          </div>

          <div style={{ minWidth: 0 }}>
            <h1 className="ga-public-profile-name">{profile.displayName || profile.username}</h1>
            <div className="ga-public-profile-handle">@{profile.username}</div>
            <div className="ga-public-profile-stats">
              <span className="ga-public-profile-stat">{Number(stats.authoredPosts || 0)} posts</span>
              <span className="ga-public-profile-stat">{Number(stats.wallPosts || 0)} wall posts</span>
              <span className="ga-public-profile-stat">Public profile</span>
            </div>
          </div>

          <div className="ga-public-profile-bio">
            {profile.bio || "This member has not added a bio yet."}
          </div>
        </section>

        <div className="ga-public-profile-grid">
          <section className="ga-public-profile-panel">
            <h2>Profile Wall</h2>

            {token ? (
              <div className="ga-profile-wall-composer">
                <textarea
                  value={wallText}
                  maxLength={700}
                  onChange={(event) => setWallText(event.target.value)}
                  placeholder={
                    isOwner
                      ? "Post something on your wall…"
                      : `Post something on @${profile.username}'s profile…`
                  }
                />
                <footer>
                  <span style={{ opacity: 0.58, fontSize: 12 }}>{wallText.length}/700</span>
                  <button
                    className="ga-public-profile-btn primary"
                    type="button"
                    disabled={wallBusy || !wallText.trim()}
                    onClick={submitWallPost}
                  >
                    {wallBusy ? "Posting…" : "Post to profile"}
                  </button>
                </footer>
                {wallMessage ? <div style={{ fontSize: 13, opacity: 0.86 }}>{wallMessage}</div> : null}
              </div>
            ) : (
              <div className="ga-profile-wall-composer">
                <div style={{ lineHeight: 1.5, opacity: 0.82 }}>
                  Sign in to post on {profile.displayName}'s profile.
                </div>
                <button className="ga-public-profile-btn primary" type="button" onClick={() => nav("/login")}>
                  Sign in
                </button>
              </div>
            )}

            {wallLoading ? (
              <div style={{ opacity: 0.72 }}>Loading profile posts…</div>
            ) : posts.length ? (
              <div className="ga-profile-wall-list">
                {posts.map((post) => (
                  <WallPost
                    key={post.id}
                    post={post}
                    token={token}
                    currentUserId={currentUserId}
                    profile={profile}
                    onDeleted={(postId) => {
                      setPosts((current) => current.filter((item) => String(item.id) !== String(postId)));
                      setStats((current) => ({
                        ...current,
                        wallPosts: Math.max(0, Number(current.wallPosts || 0) - 1),
                      }));
                    }}
                  />
                ))}
              </div>
            ) : (
              <div style={{ opacity: 0.72 }}>No one has posted on this profile yet.</div>
            )}
          </section>

          <aside className="ga-public-profile-panel">
            <h2>Links</h2>
            {socials.length ? (
              <div className="ga-public-profile-socials">
                {socials.map(([key, value]) => (
                  <a key={key} href={externalHref(value)} target="_blank" rel="noreferrer">
                    <span style={{ textTransform: "capitalize" }}>{key}</span>
                    <span style={{ color: "var(--profile-accent)" }}>Open ↗</span>
                  </a>
                ))}
              </div>
            ) : (
              <div style={{ opacity: 0.7, lineHeight: 1.5 }}>No social links added yet.</div>
            )}

            <div style={{ height: 1, background: "rgba(255,255,255,.10)", margin: "18px 0" }} />

            <h2>Community</h2>
            <div style={{ opacity: 0.74, lineHeight: 1.55, fontSize: 13 }}>
              Posts made here also appear in the GAStreaming Feed. There is no private messaging attached to profiles.
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
